'use client'

import { createContext, useEffect, useCallback, useRef } from 'react'
import axios, { AxiosInstance } from 'axios'

import { useAPIToken } from '@dc-shared-web/providers/APITokenProvider'

import { TESTING_LOCAL_ENDPOINT } from '@dc-shared-web/lib/constants'

type ApiClientContextConfig = {
  withCredentials?: boolean
  disableXRequestedWith?: boolean
}

// Provide initial api client without baseURL so that the api hooks don't
// don't throw errors about apiClient being undefined
const initialApiClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

const getAxiosConfig = (
  token: string | undefined,
  baseURL: string | undefined,
  withCredentials: boolean,
  disableXRequestedWith: boolean,
  config?: any
) => ({
  ...config,
  withCredentials,
  headers: {
    ...config?.headers,
    'Content-Type': config?.headers['Content-Type'] ?? 'application/json',
    Authorization: token ? `Bearer ${token}` : undefined,
    ...(disableXRequestedWith
      ? {}
      : {
          'X-Requested-With':
            config?.headers['X-Requested-With'] ?? 'XMLHttpRequest',
        }),
  },
  baseURL,
})

const CreateApiClientContext = ({
  withCredentials = false,
  disableXRequestedWith = false,
}: ApiClientContextConfig) => {
  const context = createContext<AxiosInstance>(initialApiClient)

  const Provider = ({
    children,
    publicURL,
    logoutURL,
    token,
  }: {
    children: React.ReactNode
    publicURL: string | undefined
    logoutURL?: string
    token?: string
  }) => {
    const { token: apiToken } = useAPIToken()
    const resolvedToken = token || apiToken

    const apiClient = useRef(
      axios.create(
        getAxiosConfig(
          resolvedToken,
          publicURL,
          withCredentials,
          disableXRequestedWith
        )
      )
    )

    const requestInterceptor = useCallback(
      (config: any) =>
        getAxiosConfig(
          resolvedToken,
          publicURL,
          withCredentials,
          disableXRequestedWith,
          config
        ),
      [publicURL, resolvedToken]
    )

    const responseErrorInterceptor = useCallback(
      (error: any) => {
        // Handle Unauthorized responses
        if (error.response?.status === 401) {
          // Force a logout so that Symfony clears the session - should also cause user to automatically be redirected to login page
          if (!TESTING_LOCAL_ENDPOINT) {
            console.error('AXIOS 401 error')
            window.location.href = logoutURL ?? `${publicURL}/logout`
          }
        }

        return Promise.reject(error)
      },
      [publicURL, logoutURL]
    )

    useEffect(() => {
      const client = apiClient.current
      const requestInterceptorId =
        client.interceptors.request.use(requestInterceptor)
      const reponseInterceptorId = client.interceptors.response.use(
        (response) => response,
        responseErrorInterceptor
      )

      return () => {
        client.interceptors.request.eject(requestInterceptorId)
        client.interceptors.response.eject(reponseInterceptorId)
      }
    }, [requestInterceptor, responseErrorInterceptor])

    return (
      <context.Provider value={apiClient.current}>{children}</context.Provider>
    )
  }

  return {
    context,
    Provider,
  }
}

export default CreateApiClientContext
